import { MoneyMapStatus } from '@fruitful/types';
import { router } from 'expo-router';
import { orderBy } from 'lodash';
import {
    ArrowRightIcon,
    BodyMediumEmphasized,
    BodySmallRegular,
    FullScreenSpinner,
    Icon,
    SafeAreaView,
    SmoothieScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { RoadmapScreenHeader } from '../../../../roadmap/components/RoadmapScreenHeader';
import { useGetActiveMoneyMapWithIncomeSplit } from '../../../../roadmap/hooks/useGetActiveMoneyMapWithIncomeSplit';
import { useGetMoneyMapsForMembership } from '../../../../roadmap/hooks/useGetMoneyMapsForMembership';
import { getMoneyMapAuthorshipLabel } from '../../../../roadmap/utils/moneyMapAuthorship';
import { getLiveSplitSourceMoneyMapId } from '../../../../roadmap/utils/moneyMapMemberEditEligibility';

const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

const PreviousMoneyMapsScreen = () => {
    const user = useAuthedUser();
    const { data: moneyMapsRaw, isLoading: isLoadingMoneyMaps } =
        useGetMoneyMapsForMembership({
            status: [MoneyMapStatus.INACTIVE],
        });
    const { data: activeWithSplit, isLoading: isLoadingActiveWithSplit } =
        useGetActiveMoneyMapWithIncomeSplit({});

    const liveSplitMoneyMapId = getLiveSplitSourceMoneyMapId(
        activeWithSplit?.currentIncomeSplit ?? null,
    );

    const moneyMaps = moneyMapsRaw
        ? orderBy(
              moneyMapsRaw.filter((map) => map.id !== liveSplitMoneyMapId),
              'updatedAt',
              'desc',
          )
        : undefined;

    const isLoading = isLoadingMoneyMaps || isLoadingActiveWithSplit;

    return (
        <SafeAreaView edges={['left', 'right', 'bottom']}>
            <RoadmapScreenHeader
                title="Previous Money Maps"
                hasBackButton={true}
            />
            {isLoading ? (
                <FullScreenSpinner />
            ) : (
                <SmoothieScrollView
                    containerStyle={{
                        width: '100%',
                        maxWidth: 700,
                        alignSelf: 'center',
                        paddingHorizontal: 24,
                    }}
                >
                    <YStack paddingVertical={24}>
                        {moneyMaps == null || moneyMaps.length === 0 ? (
                            <BodySmallRegular
                                color="$gray1000"
                                textAlign="center"
                            >
                                No previous Money Maps
                            </BodySmallRegular>
                        ) : (
                            <YStack
                                backgroundColor="$gray200"
                                borderRadius={16}
                                overflow="hidden"
                            >
                                {moneyMaps.map((map, index) => (
                                    <View
                                        key={map.id}
                                        paddingVertical={16}
                                        paddingHorizontal={16}
                                        cursor="pointer"
                                        onPress={() => {
                                            router.push({
                                                pathname:
                                                    '/roadmap/previous-money-maps/[moneyMapId]',
                                                params: {
                                                    moneyMapId: map.id,
                                                },
                                            });
                                        }}
                                        borderBottomWidth={
                                            index < moneyMaps.length - 1 ? 1 : 0
                                        }
                                        borderBottomColor="$gray400"
                                    >
                                        <XStack
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <YStack gap={2} flex={1}>
                                                <BodyMediumEmphasized>
                                                    {formatDate(
                                                        map.updatedAt ??
                                                            map.createdAt,
                                                    )}
                                                </BodyMediumEmphasized>
                                                <BodySmallRegular color="$gray1000">
                                                    {getMoneyMapAuthorshipLabel(
                                                        map,
                                                        user,
                                                    )}
                                                </BodySmallRegular>
                                            </YStack>
                                            <Icon
                                                icon={ArrowRightIcon}
                                                size={20}
                                                color="$gray1200"
                                            />
                                        </XStack>
                                    </View>
                                ))}
                            </YStack>
                        )}
                    </YStack>
                </SmoothieScrollView>
            )}
        </SafeAreaView>
    );
};

export default PreviousMoneyMapsScreen;
